// Spawning a job's ground objects, shared by createSim and the board (P3c:
// the board checks a clearance's actual leaves before offering it). Kept out
// of sim.ts so contract.ts can use it without an import cycle.

import type { Contract, GroundObject, Layout } from './types';
import { buildGrid, cellAt, T_BED, T_GRASS, T_PAVED, type SimGrid } from './layout';
import { contractLayout } from './mutations';
import { mulberry32, pick, type Rng } from './rng';
import { spawnLeaves } from './leaves';
import { GROUND_ITEMS, GROUND_ITEM_KIND } from './vibe';

const OBJECT_KINDS: GroundObject['kind'][] = ['stick', 'newspaper', 'school-hat', 'drink-bottle', 'wallet', 'brick', 'tennis-ball', 'frisbee', 'dog-toy'];
const SPORT_KINDS: GroundObject['kind'][] = ['cricket-ball', 'footy', 'drink-bottle', 'school-hat', 'stick', 'newspaper'];

/**
 * `named` (Feature P9, SimOptions.groundItems): draw from the named items
 * (thong, sunnies, …) instead. Same rng draws either way, so the leaves that
 * spawn after the objects land in the same places.
 */
export function spawnObjects(layout: Layout, grid: SimGrid, rng: Rng, n: number, lostBall: boolean, named = false): GroundObject[] {
  const cands: number[] = [];
  const cm = grid.cellM;
  const clear = Math.max(3, 2 * cm);
  for (let i = 0; i < grid.type.length; i++) {
    if (grid.type[i] !== T_GRASS || grid.dnm[i]) continue;
    const x = ((i % grid.w) + 0.5) * cm;
    const y = (Math.floor(i / grid.w) + 0.5) * cm;
    if (Math.hypot(x - layout.spawn.x, y - layout.spawn.y) < clear) continue;
    cands.push(i);
  }
  const out: GroundObject[] = [];
  if (!cands.length) return out;
  const sporty = layout.locationGroup === 'ovals' || layout.locationGroup === 'clubs';
  const place = (id: string, kind: GroundObject['kind']) => {
    const i = cands[Math.floor(rng() * cands.length)];
    out.push({ id, kind, x: ((i % grid.w) + 0.2 + 0.6 * rng()) * cm, y: (Math.floor(i / grid.w) + 0.2 + 0.6 * rng()) * cm, state: 'ground' });
  };
  for (let k = 0; k < n; k++) {
    if (!named) place(`obj-${k}`, pick(rng, sporty ? SPORT_KINDS : OBJECT_KINDS));
    else {
      const item = pick(rng, GROUND_ITEMS);
      place(`obj-${k}`, GROUND_ITEM_KIND[item]);
      out[out.length - 1].item = item;
    }
  }
  if (lostBall) {
    place('obj-lost-ball', sporty ? 'cricket-ball' : pick(rng, ['cricket-ball', 'footy'] as const));
    if (named) out[out.length - 1].item = out[out.length - 1].kind as 'cricket-ball' | 'footy';
  }
  return out;
}

/**
 * Feature P3c: the blow task's % at the start of a job — leaves not loose on
 * the paving — spawned exactly as createSim spawns them (same rng order), so
 * the board can drop a clearance that would start already done.
 */
export function blowStartPct(base: Layout, contract: Pick<Contract, 'seed' | 'mutations' | 'tasks' | 'complexity'>): number {
  const { layout, hazards } = contractLayout(base, contract);
  const rng = mulberry32(contract.seed >>> 0);
  const grid = buildGrid(layout, rng);
  const hasGround = contract.tasks.some((t) => t.kind === 'ground-check');
  spawnObjects(layout, grid, rng, hasGround ? contract.complexity + 1 : 0, hazards.lostBall);
  const L = spawnLeaves(layout, grid, rng);
  if (!L.count) return 100;
  let loose = 0;
  for (let k = 0; k < L.count; k++) {
    const i = cellAt(grid, L.x[k], L.y[k]);
    if (i >= 0 && grid.type[i] === T_PAVED) loose++;
  }
  return (1 - loose / L.count) * 100;
}

/**
 * Feature P6b: how many of a job's starting leaves lie on bed cells, spawned
 * exactly as createSim spawns them (same rng order). With debris rules only
 * the rake gets those out, so the board won't offer a clear or collect on
 * them to a shed without one.
 */
export function bedStartLeaves(base: Layout, contract: Pick<Contract, 'seed' | 'mutations' | 'tasks' | 'complexity'>): number {
  const { layout, hazards } = contractLayout(base, contract);
  const rng = mulberry32(contract.seed >>> 0);
  const grid = buildGrid(layout, rng);
  const hasGround = contract.tasks.some((t) => t.kind === 'ground-check');
  spawnObjects(layout, grid, rng, hasGround ? contract.complexity + 1 : 0, hazards.lostBall);
  const L = spawnLeaves(layout, grid, rng);
  let n = 0;
  for (let k = 0; k < L.count; k++) {
    const i = cellAt(grid, L.x[k], L.y[k]);
    if (i >= 0 && grid.type[i] === T_BED) n++;
  }
  return n;
}
